import * as zod from 'zod';

import { comboboxSchema } from '~/components/Combobox';

enum Errors {
  FirstNameRequired = 'firstNameRequired',
  LastNameRequired = 'lastNameRequired',
  UserTypeRequired = 'userTypeRequired',
  EmailRequired = 'emailRequired',
  EmailInvalid = 'emailInvalid',
  PhoneRequired = 'phoneRequired',
  PhoneInvalid = 'phoneInvalid',
  RoleRequired = 'roleRequired',
  LabelGroupRequired = 'labelGroupRequired',
  LabelsRequired = 'labelsRequired',
  JobTitleRequired = 'jobTitleRequired',
  OrganizationRequired = 'organizationRequired',
}

const commonUserFormSchema = zod.object({
  firstName: zod
    .string({ required_error: Errors.FirstNameRequired })
    .nonempty(Errors.FirstNameRequired),
  lastName: zod
    .string({ required_error: Errors.LastNameRequired })
    .nonempty(Errors.LastNameRequired),
  jobTitle: zod
    .string({ required_error: Errors.JobTitleRequired })
    .nonempty(Errors.JobTitleRequired),
  email: zod
    .string({ required_error: Errors.EmailRequired })
    .email(Errors.EmailInvalid),
  phone: zod
    .string({ required_error: Errors.PhoneRequired })
    .nonempty(Errors.PhoneInvalid),
  internalPermissions: zod.any().optional(),
  externalPermissions: zod.any().optional(),
});

const internalUserFormSchema = commonUserFormSchema.merge(
  zod.object({
    userType: zod.literal('internal'),
    organization: zod
      .string({ required_error: Errors.OrganizationRequired })
      .nonempty(Errors.OrganizationRequired),
    internalPermissions: zod.object({
      roles: zod
        .array(comboboxSchema, { required_error: Errors.RoleRequired })
        .min(1, { message: Errors.RoleRequired }),
      labelGroups: zod
        .array(comboboxSchema, {
          required_error: Errors.LabelGroupRequired,
        })
        .min(1, { message: Errors.LabelGroupRequired }),
    }),
  }),
);

const externalUserFormSchema = commonUserFormSchema.merge(
  zod.object({
    userType: zod.literal('external').default('external'),
    externalPermissions: zod.array(
      zod.object({
        role: zod
          .string({ required_error: Errors.RoleRequired })
          .nonempty(Errors.RoleRequired),
        labels: zod
          .array(comboboxSchema, { required_error: Errors.LabelsRequired })
          .min(1, { message: Errors.LabelsRequired }),
      }),
    ),
  }),
);

export const userFormSchema = zod.discriminatedUnion(
  'userType',
  [internalUserFormSchema, externalUserFormSchema],
  {
    errorMap: ({ code, path }, { defaultError }) => ({
      message:
        code === 'invalid_union_discriminator' && path[0] === 'userType'
          ? Errors.UserTypeRequired
          : defaultError,
    }),
  },
);

export type UserFormSchema = zod.infer<typeof userFormSchema>;

export const userFormSchemaForExternalUser = externalUserFormSchema;

export type UserFormSchemaForExternalUser = zod.infer<
  typeof userFormSchemaForExternalUser
>;
